import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { translate } from '~/training/sequence/templates/index';
import { Root } from '~/training/sequence/templates/items/root/Root';
import { MediaPlayerProvider } from '~/ui/video-player/MediaPlayerProvider';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { SequenceProvider } from '~/training/sequence/SequenceStore';
import { Header } from '~/editor/header/Header';
import { Overview } from '~/editor/overview/Overview';
import { ScriptTranslationView } from '~/editor/translation/ScriptTranslationView';
import { useEditorStore, EditorTraining } from '~/shell/Store';
import { EditorBanners } from '~/editor/ui/EditorBanners';

const Wrapper = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: auto;
`;

type EditorMode = 'editor' | 'preview' | 'translation';

interface MainContentProps {
  mode: string;
  setMode: (newMode: EditorMode) => void;
  exitRedirectUrl: string;
  showButtons: boolean;
  publishButtonTitle?: string | null;
  publishRedirectUrl?: string | null;
  showMenu: boolean;
  translationsOnly?: boolean;
  editingMode?: 'private' | 'live' | null;
  translationsLockedUntilPublish?: boolean;
  closeIconOnRight?: boolean;
}

export const MainContent: React.FC<MainContentProps> = ({
  mode,
  setMode,
  exitRedirectUrl,
  showButtons,
  publishButtonTitle,
  publishRedirectUrl,
  showMenu,
  translationsOnly = false,
  editingMode = null,
  translationsLockedUntilPublish = false,
  closeIconOnRight = false,
  ...restProps
}) => {
  const [content, setContent] = useState<null | JSX.Element>(null);
  const { sequence, forceUpdateSequence, setForceUpdateSequence } =
    useEditorStore();

  useEffect(() => {
    if (forceUpdateSequence) {
      if (mode === 'editor' && restProps) {
        setContent(renderItems(restProps));
      } else if (mode !== 'translation') {
        if (restProps) {
          setContent(
            <Overview data={restProps as unknown as EditorTraining} />
          );
        }
      }
      setForceUpdateSequence(false);
    }
  }, [mode, restProps, sequence, forceUpdateSequence]);

  function addTypename(obj: unknown): void {
    if (typeof obj !== 'object' || obj === null) return;
    const record = obj as Record<string, unknown>;

    for (const key in record) {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        if (key === 'type' && typeof record[key] === 'string') {
          const typeValue = record[key] as string;
          record['__typename'] = typeValue
            .toLowerCase()
            .split('_')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
        }
        addTypename(record[key]);
      }
    }
  }

  const renderItems = (el: Record<string, unknown>) => {
    addTypename(el);

    const RootComponent = new Root(
      translate(el as unknown as Parameters<typeof translate>[0])
    ).component();

    return (
      <MediaPlayerProvider>
        <AudioPlayer />
        <SequenceProvider>
          <RootComponent key={`editor_${el.id}`} />
        </SequenceProvider>
      </MediaPlayerProvider>
    );
  };

  useEffect(() => {
    if (mode === 'editor' && restProps) {
      setContent(renderItems(restProps));
    } else if (mode !== 'translation') {
      if (restProps) {
        setContent(<Overview data={restProps as unknown as EditorTraining} />);
      }
    }
  }, [mode]);

  return (
    <Wrapper id="mainContentInTheCourseBuilder">
      {showButtons && (
        <Header
          mode={mode}
          setMode={(newMode: EditorMode) => setMode(newMode)}
          exitRedirectUrl={exitRedirectUrl}
          publishButtonTitle={publishButtonTitle}
          publishRedirectUrl={publishRedirectUrl}
          translationsOnly={translationsOnly}
          editingMode={editingMode}
          translationsLockedUntilPublish={translationsLockedUntilPublish}
          closeIconOnRight={closeIconOnRight}
        />
      )}
      {mode === 'translation' ? <ScriptTranslationView /> : content}
      {mode === 'editor' && <EditorBanners showMenu={showMenu} />}
    </Wrapper>
  );
};
